import { Subject, BehaviorSubject, Observable, debounceTime, filter, combineLatest, map, startWith } from 'rxjs';
import { Attachment } from './attachment';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { AttachmentStorage } from './attachment-storage';
import { AttachmentSyncManager } from './attachment-sync-manager';
import { AttachmentMigration } from './attachment-migration';

/**
 * Sync status summary for UI display
 */
export interface AttachmentSyncSummary {
    isUploading: boolean;
    pendingCount: number;
    failedCount: number;
    uploadingCount: number;
    hasIssues: boolean;
}

export class Attachment$ {
    private _id2AttMp = new Map<string, Attachment>();
    private _targetId2AttMp = new Map<string, Set<Attachment>>();
    private _editMap = new Map<string, Attachment>();

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    // IndexedDB storage for attachment blobs
    private _storage: AttachmentStorage;
    
    // Cloud sync manager
    private _syncManager: AttachmentSyncManager;

    // Migration handler for OPFS to IndexedDB
    private _migration: AttachmentMigration;

    constructor(private coords: CoordinatorService) {
        // Initialize IndexedDB storage
        this._storage = new AttachmentStorage(coords);
        
        // Initialize sync manager
        this._syncManager = new AttachmentSyncManager(coords, this._storage);

        // Initialize migration handler
        this._migration = new AttachmentMigration(coords, this._storage);

        this._markForManifestStore$.subscribe(() => this._manifestStore());
        
        // Subscribe to sync status changes for UI updates
        this._syncManager.uploadProgress$.subscribe(({ id, projectId, progress }) => {
            console.log(`Attachment ${id} sync: ${progress}`);
            this.coords.display$.markForCheck();
        });

        // Subscribe to storage errors
        this._storage.error$.subscribe((error) => {
            console.error('AttachmentStorage error:', error);
            this.coords.display$.error('attachment-storage', error);
        });

        // Initialize sync status tracking for UI
        this._initSyncStatusTracking();
    }

    /**
     * Get the IndexedDB storage instance
     */
    get storage(): AttachmentStorage {
        return this._storage;
    }

    /**
     * Get the sync manager instance
     */
    get syncManager(): AttachmentSyncManager {
        return this._syncManager;
    }

    /**
     * Get the migration handler
     */
    get migration(): AttachmentMigration {
        return this._migration;
    }

    /**
     * Check if storage is ready
     */
    get isStorageReady(): boolean {
        return this._storage.isReady;
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    get canAttach() {
        return (
            !this.coords.project$.current?.isCloud ||
            this.coords.project$.current?.team?.currentUser?.canAttach
        );
    }

    get attachments() {
        return Array.from(this._id2AttMp.values());
    }

    attachmentLs(itmId: string) {
        return Array.from(this._targetId2AttMp.get(itmId) || []);
    }

    add(att: Attachment) {
        this._id2AttMp.set(att.id, att);
        let s = this._targetId2AttMp.get(att.targetId);
        if (!s) {
            s = new Set<Attachment>();
            this._targetId2AttMp.set(att.targetId, s);
        }
        s.add(att);
    }

    delete(att: Attachment) {
        this._id2AttMp.delete(att.id);
        this._targetId2AttMp.get(att.targetId)?.delete(att);
    }

    createDialog(targetId: string, isStandalone = false) {
        const att = new Attachment(this.coords, { targetId: targetId });
        this._editMap.clear();
        this._editMap.set(att.id, att);
        this.coords.display$.dialog$.next({
            action: 'attachment',
            attachment: att,
            isStandalone: isStandalone,
        });
    }

    editDialog(att: Attachment) {
        this._editMap.clear();
        this._editMap.set(att.id, att);
        this.coords.display$.dialog$.next({
            action: 'edit-attachment',
            attachment: att,
        });
    }

    private _manifestStore() {
        this.coords.manifest$.store({
            type: 'attachment',
            id: `attachmentLs_${this.coords.project$.currentId}`,
            valueMap: new Map<string, any[]>([['attachmentLs', this.storeLs(false)]]),
        });
    }

    storeLs(isReportProject: boolean) {
        let ls = Array.from(this._id2AttMp.values());
        if (!isReportProject) ls = ls.filter((att) => att.isOwned);
        return ls.map((att) => att.save());
    }

    load3(params: any) {
        if (!params) return;
        try {
            // CRITICAL: Use unload() instead of delete() to avoid:
            // 1. Deleting blobs from IndexedDB that we still need
            // 2. Triggering manifest store with empty/partial data
            for (const a of Array.from(this._id2AttMp.values())) {
                if (a.ownerId == params.ownerId) a.unload();
            }

            for (const a of params.attachmentLs) {
                a.ownerId = params.ownerId;
                const att = new Attachment(this.coords, a);
                this.add(att);
            }

            // Trigger migration from OPFS to IndexedDB
            this._triggerMigration(params.attachmentLs || []);
        } catch (e) {
            this.coords.utils$.consoleError('ATTACHMENT LOAD3 ERROR', e);
        }
    }

    /**
     * Trigger migration from OPFS to IndexedDB for loaded attachments
     */
    private async _triggerMigration(attachmentLs: any[]) {
        const projectId = this.coords.project$.currentId;
        if (!projectId) return;

        // Wait for storage to be ready
        if (!this._storage.isReady) {
            this._storage.isReady$.pipe(
                filter(ready => ready)
            ).subscribe(() => {
                this._runMigration(projectId, attachmentLs);
            });
        } else {
            this._runMigration(projectId, attachmentLs);
        }
    }

    /**
     * Run the actual migration and sync pending uploads for cloud projects
     */
    private async _runMigration(projectId: string, attachmentLs: any[]) {
        // Extract cachenames and metadata from attachments
        const migrationData = attachmentLs
            .filter((a: any) => a.values?.cachename)
            .map((a: any) => ({
                cachename: a.values.cachename,
                filename: a.values.filename,
                mimetype: a.values.mimetype,
                size: a.values.size
            }));

        if (migrationData.length > 0) {
            const result = await this._migration.migrateProject(projectId, migrationData);
            if (result.migrated > 0) {
                console.log('%c Attachments migrated to IndexedDB', 'background: green; color: white;', result);
            }
        }

        // CRITICAL: For cloud projects, automatically sync pending/failed uploads
        // This ensures attachments are uploaded even if user closed browser before upload completed
        if (this.coords.project$.inCloud) {
            const syncCount = await this.syncPendingUploads();
            if (syncCount > 0) {
                console.log('%c Resuming attachment uploads', 'color: orange;', { count: syncCount });
            }
        }
    }

    /**
     * Queue all pending uploads for the current project
     * Call this when a project becomes a cloud project or on project load
     */
    async syncPendingUploads(): Promise<number> {
        const projectId = this.coords.project$.currentId;
        if (!projectId || !this.coords.project$.inCloud) {
            return 0;
        }
        
        return await this._syncManager.queueProjectUploads(projectId);
    }

    /**
     * Get sync statistics for the current project
     */
    async getSyncStats(): Promise<{
        total: number;
        synced: number;
        pending: number;
        uploading: number;
        failed: number;
    } | null> {
        const projectId = this.coords.project$.currentId;
        if (!projectId) return null;
        
        return await this._syncManager.getProjectSyncStats(projectId);
    }

    /**
     * Retry all failed uploads for the current project
     */
    async retryFailedUploads(): Promise<number> {
        const projectId = this.coords.project$.currentId;
        if (!projectId) return 0;
        
        return await this._syncManager.retryFailedUploads(projectId);
    }

    /**
     * Check if all attachments are synced for the current project
     */
    async isFullySynced(): Promise<boolean> {
        const projectId = this.coords.project$.currentId;
        if (!projectId) return true;
        
        if (!this.coords.project$.inCloud) {
            return true; // Local projects are always "synced"
        }
        
        return await this._storage.isProjectFullySynced(projectId);
    }

    /**
     * Delete all attachment blobs for a project
     * Call this when deleting a project
     */
    async deleteProjectAttachments(projectId: string): Promise<number> {
        return await this._storage.deleteByProject(projectId);
    }

    /**
     * Check if there are active uploads
     */
    get hasActiveUploads(): boolean {
        return this._syncManager.hasActiveUploads;
    }

    /**
     * Get all attachment records from IndexedDB for a project
     */
    async getStorageRecords(projectId: string) {
        return await this._storage.getByProject(projectId);
    }

    // Cache for sync status to avoid repeated DB queries
    private _syncStatusCache$ = new BehaviorSubject<AttachmentSyncSummary>({
        isUploading: false,
        pendingCount: 0,
        failedCount: 0,
        uploadingCount: 0,
        hasIssues: false
    });

    /**
     * Observable for sync status summary (for UI display)
     * Emits whenever upload progress changes
     */
    get syncStatus$(): Observable<AttachmentSyncSummary> {
        return this._syncStatusCache$.asObservable();
    }

    /**
     * Get current sync status summary (synchronous)
     */
    get syncStatus(): AttachmentSyncSummary {
        return this._syncStatusCache$.getValue();
    }

    /**
     * Update sync status cache (called internally when uploads change)
     */
    private async _updateSyncStatusCache(): Promise<void> {
        const projectId = this.coords.project$.currentId;
        if (!projectId || !this.coords.project$.inCloud) {
            this._syncStatusCache$.next({
                isUploading: false,
                pendingCount: 0,
                failedCount: 0,
                uploadingCount: 0,
                hasIssues: false
            });
            return;
        }

        try {
            const stats = await this._syncManager.getProjectSyncStats(projectId);
            this._syncStatusCache$.next({
                isUploading: this._syncManager.hasActiveUploads,
                pendingCount: stats.pending,
                failedCount: stats.failed,
                uploadingCount: stats.uploading,
                hasIssues: stats.failed > 0 || stats.pending > 0
            });
        } catch (e) {
            // Ignore errors, just keep the previous state
        }
    }

    /**
     * Subscribe to upload progress and update the cache
     * (Called from constructor)
     */
    private _initSyncStatusTracking(): void {
        // Update cache whenever upload progress changes
        this._syncManager.uploadProgress$.subscribe(() => {
            this._updateSyncStatusCache();
        });
        
        // Also update when sync status changes in storage
        this._storage.syncStatusChange$.subscribe(() => {
            this._updateSyncStatusCache();
        });
    }
}
